import React from "react";
import { useNavigate } from "react-router-dom";
import Star from "../../../components/Atomos/Icons/Star";
import HandShake from "../../../assets/images&icons/hands-referral.svg";
import LogoFooter from "../../../assets/images&icons/logo-wsg.svg";
import { buildFirmNameForUrl } from "../../../utils/firmNames";
import MyWsgCardBadge from "../Components/MyWsgCardBadge";

export interface IReferralCard {
  id: string;
  legacyId: number;
  firmByName?: string;
  firmToName?: string;
  firmById?: string;
  firmToId?: string;
  estimatedFees: number;
  estimatedFeesDetail?: string;
  submissionDate: string;
  type: number;
  referralType: string;
  image?: string[];
  firmByLogo?: string;
  firmToLogo?: string;
  relationshipType?: string;
}

interface IData {
  data: IReferralCard[];
  userFirmLogo?: string;
  userFirmName?: string;
}
const Referrals: React.FC<IData> = ({ data = [], userFirmLogo, userFirmName }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (  
      <div className="flex flex-col gap-4 text-gray-font justify-center items-center h-full">
        <ReferralLogos
          logoFrom={userFirmLogo || LogoFooter}
          nameFrom={userFirmName}
          logoTo={undefined}
          nameTo={undefined}
        />
        <div className="text-center py-4 roboto text-[#464F56]">You don’t have any referral or collaboration yet.</div>
        <div className="text-center py-4 roboto text-[#464F56]">Grow your influence adding one today.</div>
      </div>
    );
  }

  const referralCard = (ref: IReferralCard, index: number) => {
    if (!ref) {
      return null;
    }
    
    const formatDate = (date: string) => {
      try {
        return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      } catch (e) {
        return 'Invalid date';
      }
    };

    const formatRelationshipType = (type?: string) => {
      if (!type) {
        return "Unknown";
      }
      return type.charAt(0).toUpperCase() + type.slice(1);
    };

    return (
      <button
        className="flex flex-col gap-1"
        onClick={() => navigate(`/manage_referrals.asp?action=detail&id=${ref.legacyId}`,)}
      >
        <div className={`flex flex-row flex-wrap`}>
          <div className={`flex ${index > 0 ? "w-fit" : "w-full"} gap-2 text-left`}>
            Referred by:<span className="font-semibold hover:text-primary" onClick={(e) => {
              e.stopPropagation();
              navigate(`/member/law-firm/${buildFirmNameForUrl(ref.firmByName || '')}/${ref.firmById}`,)}}>{ref.firmByName ?? "Unknown"}</span>
          </div>
          {index > 0 &&  <div className="w-px bg-primary-light mx-2 hidden sm:block" />}
          <div className="flex w-fit gap-2 text-left">
            Referred to: <span className="font-semibold hover:text-primary" onClick={(e) => {
              e.stopPropagation();
              navigate(`/member/law-firm/${buildFirmNameForUrl(ref.firmToName || '')}/${ref.firmToId}`,)}}>{ref.firmToName ?? "Unknown"}</span>
          </div>
        </div>

        <div className="flex gap-2 text-left">
        </div>
        <div className="w-fit text-sm px-2 py-1 bg-accent-Default-light rounded-md">
          {formatRelationshipType(ref.referralType)}
        </div>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-4 text-gray-font">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col gap-2" >
          <ReferralLogos 
            logoFrom={data[0].firmByLogo || LogoFooter} 
            nameFrom={data[0].firmByName}
            logoTo={data[0].firmToLogo || LogoFooter}
            nameTo={data[0].firmToName}
          />
        </div>
        <div className="hidden sm:block w-px self-stretch bg-primary-light mr-4" />
        <div className="flex flex-col justify-center">
          {data.length > 0 && (
            <>
              {referralCard(data[0], 0)}
            </>
          )}
        </div>
      </div>
      {data.length > 1 && (
        <>
          <div className="flex items-center w-full">
            <span className="text-right text-lg md:text-2xl mr-2 font-semibold">
              Explore More Referrals & Collaborations
            </span>
            <div className="flex-1 border-t border-primary-light"></div>
          </div>
          <div>
            <div className="flex flex-col gap-4">
              {data.slice(1, 4).map((r, idx) => referralCard(r, idx + 1))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ReferralLogos = ({ 
  logoFrom, 
  nameFrom, 
  logoTo, 
  nameTo 
}: { 
  logoFrom: string | undefined, 
  nameFrom: string | undefined,
  logoTo: string | undefined,
  nameTo: string | undefined
}) => {
  return (
    <div className="relative flex items-center justify-center">
      <div className={`relative h-24 w-24 sm:h-24 sm:w-24 lg:h-[5.5rem] lg:w-[5.5rem] rounded-full border border-[#C2C2C2] overflow-hidden flex items-center justify-center ${logoFrom ? 'p-3' : ''}`}>
        {logoFrom ? (
          <img
            src={logoFrom}
            alt={nameFrom || 'From firm logo'}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-full" />
        )}
      </div>


      <div className="absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="flex items-center justify-center h-5 w-5 sm:h-5 sm:w-5 lg:h-7 lg:w-7 rounded-full bg-[#C2C2C2] border-white shadow-md">
          <img src={HandShake} alt="HandShake" className="w-3 h-3 sm:w-4 sm:h-4" />
        </div>
      </div>

      <div className={`relative h-24 w-24 sm:h-24 sm:w-24 lg:h-[5.5rem] lg:w-[5.5rem] rounded-full border border-[#C2C2C2] overflow-hidden flex items-center justify-center ${logoTo ? 'p-3' : ''}`}>
            {logoTo ? (
          <img
            src={logoTo}
            alt={nameTo || 'To firm logo'}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-full" />
        )}
      </div>

    </div>

  )
}

export default Referrals;
